import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBTPPSG4a62Z-LsARFRhTnWWyqbHjoVHpM",
  authDomain: "diet-z.firebaseapp.com",
  projectId: "diet-z",
  storageBucket: "diet-z.appspot.com",
  messagingSenderId: "387933989796",
  appId: "1:387933989796:web:f49feeaed8b72636d22f31",
  measurementId: "G-HTW8C5XJF8"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Auth
const auth = getAuth(app);

// Initialize Storage
const storage = getStorage(app);

// Auto sign-in anonymously
let authPromise = null;

/**
 * Ensure user is authenticated anonymously
 * @returns {Promise<void>}
 */
const ensureAuthenticated = async () => {
  // If already authenticating, wait for that promise
  if (authPromise) {
    return authPromise;
  }

  // Check if already signed in
  if (auth.currentUser) {
    return Promise.resolve();
  }

  // Sign in anonymously
  authPromise = signInAnonymously(auth)
    .then((userCredential) => {
      console.log('✅ Firebase Anonymous Auth successful:', userCredential.user.uid);
      authPromise = null;
    })
    .catch((error) => {
      console.error('❌ Firebase Anonymous Auth error:', error);
      authPromise = null;
      throw error;
    });

  return authPromise;
};

/**
 * Upload profile image to Firebase Storage
 * @param {File} file - Image file to upload
 * @param {string} userId - User ID (or NID/username)
 * @returns {Promise<string>} Download URL of uploaded image
 */
export const uploadProfileImage = async (file, userId) => {
  try {
    if (!file) {
      throw new Error('ไม่พบไฟล์รูปภาพ');
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      throw new Error('รองรับเฉพาะไฟล์ JPG, PNG, และ WEBP เท่านั้น');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('ขนาดไฟล์ต้องไม่เกิน 5MB');
    }

    // Ensure authenticated before upload
    await ensureAuthenticated();

    // Create unique filename
    const timestamp = Date.now();
    const extension = file.name.split('.').pop();
    const filename = `profile_${userId}_${timestamp}.${extension}`;

    // Create storage reference
    const storageRef = ref(storage, `profile-images/${filename}`);

    // Upload file
    console.log('📤 Uploading profile image:', filename);
    const snapshot = await uploadBytes(storageRef, file);
    console.log('✅ Upload successful:', snapshot.metadata.fullPath);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('🔗 Download URL:', downloadURL);

    return downloadURL;
  } catch (error) {
    console.error('❌ Upload profile image error:', error);
    throw error;
  }
};

/**
 * Delete profile image from Firebase Storage
 * @param {string} imageUrl - Full URL of image to delete
 * @returns {Promise<void>}
 */
export const deleteProfileImage = async (imageUrl) => {
  try {
    if (!imageUrl) return;

    // Ensure authenticated before delete
    await ensureAuthenticated();

    // Extract path from URL
    const path = imageUrl.split('/o/')[1]?.split('?')[0];
    if (!path) {
      console.warn('Cannot extract path from URL:', imageUrl);
      return;
    }

    const decodedPath = decodeURIComponent(path);
    const storageRef = ref(storage, decodedPath);

    console.log('🗑️ Deleting profile image:', decodedPath);
    await deleteObject(storageRef);
    console.log('✅ Delete successful');
  } catch (error) {
    console.error('❌ Delete profile image error:', error);
    // Don't throw error - deletion failure shouldn't block other operations
  }
};

/**
 * Compress and resize image before upload
 * @param {File} file - Image file to compress
 * @param {number} maxWidth - Maximum width (default: 800px)
 * @param {number} maxHeight - Maximum height (default: 800px)
 * @param {number} quality - Image quality 0-1 (default: 0.8)
 * @returns {Promise<Blob>} Compressed image blob
 */
export const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              console.log('✅ Image compressed:', {
                originalSize: `${(file.size / 1024).toFixed(2)}KB`,
                compressedSize: `${(blob.size / 1024).toFixed(2)}KB`,
                originalDimensions: `${img.width}x${img.height}`,
                newDimensions: `${width}x${height}`
              });
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Upload profile image with compression
 * @param {File} file - Image file to upload
 * @param {string} userId - User ID
 * @returns {Promise<string>} Download URL
 */
export const uploadCompressedProfileImage = async (file, userId) => {
  try {
    // Compress image first
    const compressedBlob = await compressImage(file);

    // Convert blob to file
    const compressedFile = new File(
      [compressedBlob],
      file.name,
      { type: file.type }
    );

    // Upload compressed file
    return await uploadProfileImage(compressedFile, userId);
  } catch (error) {
    console.error('❌ Upload compressed image error:', error);
    throw error;
  }
};

export { storage };
